import {
  prop,
  always,
  converge,
  path,
  ifElse,
  equals,
  split,
  last,
  compose,
  isNil,
  identity,
  includes,
  and,
  not,
  defaultTo,
  replace,
  head,
  T,
  cond,
} from 'ramda';
import { matchPath } from 'react-router-dom';
import { parse } from 'query-string';

import {
  DEFAULT_APP_NAME,
  SUCCESS_PATH,
  OFFER_SUCCESS_PATH,
  BILLING_PATH,
  HOME_PATH,
  LOGIN_PATH,
  REGISTER_PATH,
  UNAVAILABLE_PATH,
  ALREADY_PREMIUM_PATH,
  XBOX_LANDING_PATH,
  NEXT,
  HOME_PAGE_QUERY,
  REGISTRATION_PROMO_PATH,
  PRICING_PROMO_LANDING_PATH,
  PRICING_PROMO_SUCCESS_PATH,
  MIGRATION_LANDING_PATH,
  MIGRATION_SUCCESS_PATH,
  COUPON_APP_NAME,
  OFFER_APP_NAME,
} from 'app/router/constants/paths';
import {
  userAccessType,
  isDirectToPay,
  userLocale,
} from 'app/features/user/selectors';
import { landingPageName } from 'app/features/landing/selectors';
import { buildPathRegex } from 'app/router/helpers/buildPathRegex';
import {
  ACQUISITION,
  CAMPAIGN_ACQUISITION_FUNNEL,
  COUPON_CAMPAIGN,
} from 'app/features/deltaConfig/constants';
import {
  FAN,
  SUPER_FAN,
  PREMIUM,
  PREMIUM_PLUS,
} from 'app/features/user/constants/accessTypes';
import { routerLocales } from 'app/router/constants/routerLocales';
import { FROM, REF, REFERRER, SRC } from 'app/router/constants/urlParameters';
import { CRUNCHYROLL } from 'app/constants/constants';
import { LOGIN_URL } from 'app/router/constants/externalUrls';
import localizeLink from 'app/features/localization/localizeLink';

const router = prop('router');

const getLocation = compose(prop('location'), router);

const referrer = compose(prop('referrer'), router);

const referrerURL = compose(prop('referrerURL'), router);

const isNotFoundRoute = compose(prop('isNotFound'), router);

const getPathname = compose(prop('pathname'), getLocation);

const locationState = compose(prop('state'), getLocation);

const getAppName = compose(
  ifElse(isNil, always(DEFAULT_APP_NAME), identity),
  path(['params', 'app']),
  converge(matchPath, [getPathname, always({ path: buildPathRegex('/') })]),
);

const isOfferApp = compose(
  equals(OFFER_APP_NAME),
  compose(head, split('-')),
  getAppName,
);

const getOfferId = ifElse(
  isOfferApp,
  compose(last, split('-'), getAppName),
  always(undefined),
);

const isCouponApp = compose(
  equals(COUPON_APP_NAME),
  compose(head, split('-')),
  getAppName,
);

const getCouponCampaignId = ifElse(
  isCouponApp,
  compose(last, split('-'), getAppName),
  always(undefined),
);

const isCampaignSuccessRoute = compose(not, equals(undefined), getOfferId);

const selectAppConfigKey = cond([
  [isCouponApp, always(COUPON_CAMPAIGN)],
  [isOfferApp, always(CAMPAIGN_ACQUISITION_FUNNEL)],
  [T, always(ACQUISITION)],
]);

const getLocationHash = compose(prop('hash'), getLocation);

const getLocationHref = compose(
  ({ pathname, search, hash }) =>
    `${window.location.origin}${pathname}${search}${hash}`,
  getLocation,
);

const pageNameFor = (pathname, landingPageName) => {
  const pageNameMap = {
    [XBOX_LANDING_PATH]: 'Offer Landing page',
    [MIGRATION_LANDING_PATH]: 'Content Migration Offer Landing page',
    [MIGRATION_SUCCESS_PATH]: 'Content Migration Offer Success Page',
    [SUCCESS_PATH]: 'Checkout Success',
    [OFFER_SUCCESS_PATH]: 'Offer Checkout Complete',
    [BILLING_PATH]: 'Billing Cycle Selection',
    [LOGIN_PATH]: 'Login',
    [REGISTER_PATH]: 'Registration',
    [ALREADY_PREMIUM_PATH]: 'Checkout Success',
    [REGISTRATION_PROMO_PATH]: 'Registration funnel: Free trial prompt',
    [PRICING_PROMO_LANDING_PATH]: 'Pricing Promo Landing page',
    [PRICING_PROMO_SUCCESS_PATH]: 'Pricing Promo Success',
    [HOME_PATH]: landingPageName,
  };

  const key = Object.keys(pageNameMap).find(
    k => pathname && pathname.includes(k),
  );

  return key ? pageNameMap[key] : pathname;
};

const pageNameFromRouterPath = converge(pageNameFor, [
  getPathname,
  landingPageName,
]);

const pageTitleFor = (pathname, accessType) => {
  const accessTypesMap = {
    [FAN]: 'Mega Fan',
    [SUPER_FAN]: 'Ultimate Fan',
    [PREMIUM]: 'Fan',
    [PREMIUM_PLUS]: 'Fan',
  };
  const successPageTitle = `Crunchyroll ${accessTypesMap[accessType]} Achieved!`;
  const pageTitleMap = {
    [SUCCESS_PATH]: successPageTitle,
    [OFFER_SUCCESS_PATH]: successPageTitle,
    [BILLING_PATH]: 'Choose your billing plan',
    [ALREADY_PREMIUM_PATH]: 'Already a Crunchyroll premium user!',
  };

  const key = Object.keys(pageTitleMap).find(
    k => pathname && pathname.includes(k),
  );

  return pageTitleMap[key] || CRUNCHYROLL;
};

const pageTitleFromRouterPath = converge(pageTitleFor, [
  getPathname,
  userAccessType,
]);

const isOnUnavailablePage = compose(includes(UNAVAILABLE_PATH), getPathname);

const hasComeFromLoginPage = compose(Boolean, prop('fromLogin'), locationState);

const hasDirectToPayUserJustLoggedIn = converge(and, [
  hasComeFromLoginPage,
  isDirectToPay,
]);

const getSearch = compose(prop('search'), getLocation);

const getCampaignUnavailablePath = converge(
  (appName, search = '', hash = '') =>
    `/${appName}${UNAVAILABLE_PATH}${search}${hash}`,
  [getAppName, getSearch, getLocationHash],
);

const localizedCampaignUnavailablePath = converge(localizeLink, [
  getCampaignUnavailablePath,
  userLocale,
]);

const getDecodedParsedSearch = compose(parse, decodeURIComponent, getSearch);

const getParsedSearch = compose(parse, getSearch);

const getParsedReturnUrl = compose(prop('return_url'), getDecodedParsedSearch);

const getParsedSelectedSku = compose(
  prop('selected_sku'),
  getDecodedParsedSearch,
);

const getIsFreeTrialFlow = compose(
  Boolean,
  prop('is_free_trial_flow'),
  getDecodedParsedSearch,
);

const getMarketingReferrerFromUrl = compose(
  params => params[[REFERRER, SRC, FROM, REF].find(k => params[k])],
  getDecodedParsedSearch,
);

const getParsedNextParam = compose(prop(NEXT), getParsedSearch);

const searchReferrer = compose(prop(REFERRER), getDecodedParsedSearch);

const getPathnameWithoutLocale = compose(pathname => {
  const [_, locale, ...rest] = pathname.split('/');

  return Object.values(routerLocales).includes(`/${locale}`)
    ? [_, ...rest].join('/')
    : pathname;
}, getPathname);

const getPathnameWithoutAppName = converge(
  (appName, pathname) => pathname.replace(`/${appName}`, ''),
  [getAppName, getPathnameWithoutLocale],
);

const getRoutePathname = compose(
  replace(/\/+$/, ''),
  getPathnameWithoutAppName,
);

const getSuccessPath = ifElse(
  getOfferId,
  always(OFFER_SUCCESS_PATH),
  always(SUCCESS_PATH),
);

const isLandingPath = converge(
  (path, appName) => path === `/${appName}` || path === `/${appName}/`,
  [getPathnameWithoutLocale, getAppName],
);

const isXboxOfferPath = compose(
  path => path.includes(XBOX_LANDING_PATH),
  getPathnameWithoutLocale,
);

const isPricingPromoLandingPath = compose(
  path =>
    path === PRICING_PROMO_LANDING_PATH ||
    path === `${PRICING_PROMO_LANDING_PATH}/`,
  getPathnameWithoutAppName,
);

const routeNameFromPath = compose(
  pathname => pathname.substring(pathname.lastIndexOf('/')),
  getPathname,
);

const signInLink = compose(
  ifElse(equals(HOME_PAGE_QUERY), always(LOGIN_URL), always(LOGIN_PATH)),
  searchReferrer,
);

const windowLocaleFromPath = compose(
  defaultTo(''),
  path(['params', 'locale']),
  converge(matchPath, [
    always(window.location.pathname),
    always({ path: buildPathRegex('/') }),
  ]),
);

export {
  getLocationHref,
  getPathname,
  pageNameFromRouterPath,
  pageTitleFromRouterPath,
  getAppName,
  getOfferId,
  getCouponCampaignId,
  isCouponApp,
  isCampaignSuccessRoute,
  selectAppConfigKey,
  isOnUnavailablePage,
  hasDirectToPayUserJustLoggedIn,
  getCampaignUnavailablePath,
  getDecodedParsedSearch,
  getParsedSearch,
  getParsedReturnUrl,
  getParsedSelectedSku,
  getSuccessPath,
  getPathnameWithoutLocale,
  getLocationHash,
  isLandingPath,
  getMarketingReferrerFromUrl,
  searchReferrer,
  referrer,
  isXboxOfferPath,
  isPricingPromoLandingPath,
  routeNameFromPath,
  referrerURL,
  signInLink,
  getParsedNextParam,
  getSearch,
  windowLocaleFromPath,
  getRoutePathname,
  isNotFoundRoute,
  localizedCampaignUnavailablePath,
  getIsFreeTrialFlow,
};
